/**
 * osisUtils.ts
 *
 * Utility functions for parsing and validating OSIS (Open Scripture Information Standard) references
 */

/**
 * Checks if a single OSIS passage reference represents a full chapter (no specific verses)
 *
 * @param passageRef - OSIS reference like "Matt.1", "Isa.1-Isa.4", "Matt.1.18-Matt.1.25", etc.
 * @returns true if the reference represents full chapter(s), false if it includes specific verses
 *
 * @example
 * isSinglePassageFullChapter("Matt.1") // true - single full chapter
 * isSinglePassageFullChapter("Isa.1-Isa.4") // true - chapter range
 * isSinglePassageFullChapter("Matt.1.18-Matt.1.25") // false - verse range
 * isSinglePassageFullChapter("John.3.16") // false - single verse
 */
export function isSinglePassageFullChapter(passageRef: string): boolean {
  if (!passageRef) return false;

  const trimmed = passageRef.trim();

  // Full chapters look like:
  // - "Matt.1" (single chapter)
  // - "Gen.1-Gen.3" (chapter range)
  // - "Isa.1-Isa.4" (chapter range, can also be written as "Isa.1-4")

  // Partial passages (with verses) look like:
  // - "Matt.1.18-Matt.1.25" (verse range within chapter)
  // - "Rom.1.8-Rom.1.15" (verse range within chapter)
  // - "John.3.16" (single verse)

  // Check if it's a range
  if (trimmed.includes('-')) {
    const [start, end] = trimmed.split('-').map((s) => s.trim());

    // Check if start part has verses (more than 2 periods means it has verses)
    const startParts = start.split('.');

    if (startParts.length > 2) {
      // Has verses, not a full chapter
      return false;
    }

    // Check if end part has verses
    const endParts = end.split('.');

    if (endParts.length > 2) {
      // Has verses, not a full chapter
      return false;
    }

    // Both start and end are in Book.Chapter format, so it's a chapter range
    return true;
  }

  // Not a range, check if it's a single reference
  const parts = trimmed.split('.');

  // Book.Chapter format (full chapter)
  if (parts.length === 2) return true;

  // Book.Chapter.Verse or more (has verses, not full chapter)
  if (parts.length > 2) return false;

  // Just a book name without chapter (shouldn't happen, but treat as not full chapter)
  return false;
}

/**
 * Checks if all passages in an OSIS reference represent full chapters
 *
 * @param osisRef - OSIS reference that may contain multiple comma-separated passages
 * @returns true if all passages represent full chapters, false otherwise
 *
 * @example
 * isFullChapter("Matt.1,Matt.2") // true - all full chapters
 * isFullChapter("Matt.1.18-Matt.1.25") // false - has verses
 * isFullChapter("Matt.1,John.3.16") // false - second passage has verses
 */
export function isFullChapter(osisRef: string): boolean {
  if (!osisRef) return false;

  // Split by comma to handle multiple passages
  const passages = osisRef.split(',');

  return passages.every((passage) => {
    return isSinglePassageFullChapter(passage);
  });
}

/**
 * Extracts the full chapter reference from a single passage that may contain verses
 *
 * @param passageRef - OSIS reference like "Matt.1.18-Matt.1.25"
 * @returns Full chapter reference like "Matt.1"
 *
 * @example
 * getSinglePassageFullChapter("Matt.1.18-Matt.1.25") // "Matt.1"
 * getSinglePassageFullChapter("Matt.1.18-Matt.2.5") // "Matt.1-Matt.2"
 * getSinglePassageFullChapter("Matt.1") // "Matt.1"
 * getSinglePassageFullChapter("Isa.1-Isa.4") // "Isa.1-Isa.4"
 */
export function getSinglePassageFullChapter(passageRef: string): string {
  if (!passageRef) return '';

  const trimmed = passageRef.trim();

  // Check if it's a range
  if (trimmed.includes('-')) {
    const [start, end] = trimmed.split('-').map((s) => s.trim());

    // Extract book.chapter from start
    const startParts = start.split('.');
    const startChapter =
      startParts.length >= 2 ? `${startParts[0]}.${startParts[1]}` : start;

    // Extract book.chapter from end
    const endParts = end.split('.');
    let endChapter: string;

    if (endParts.length >= 2) {
      // Full book.chapter format or has verses
      endChapter = `${endParts[0]}.${endParts[1]}`;
    } else if (endParts.length === 1 && startParts.length >= 2) {
      // Abbreviated format like "Isa.1-4", expand to "Isa.1-Isa.4"
      endChapter = `${startParts[0]}.${endParts[0]}`;
    } else {
      endChapter = end;
    }

    // If start and end chapters are the same, return just one
    if (startChapter === endChapter) {
      return startChapter;
    }

    // Return the chapter range
    return `${startChapter}-${endChapter}`;
  }

  // Not a range, extract book.chapter
  const parts = trimmed.split('.');

  if (parts.length >= 2) {
    return `${parts[0]}.${parts[1]}`;
  }

  return trimmed;
}

/**
 * Gets the full chapter OSIS reference from an OSIS that may contain verses
 *
 * @param osisRef - OSIS reference that may contain multiple comma-separated passages
 * @returns OSIS reference with full chapters only (verses removed)
 *
 * @example
 * getFullChapterOsis("Matt.1.18-Matt.1.25,John.3.16") // "Matt.1,John.3"
 * getFullChapterOsis("Matt.1,Matt.2") // "Matt.1,Matt.2"
 */
export function getFullChapterOsis(osisRef: string): string {
  if (!osisRef) return '';

  // Split by comma to handle multiple passages
  const passages = osisRef.split(',');

  const fullChapterPassages = passages.map((passage) => {
    return getSinglePassageFullChapter(passage);
  });

  // Remove duplicates and join
  const uniqueChapters = [...new Set(fullChapterPassages)];

  return uniqueChapters.join(',');
}
